import { useState, useEffect, useCallback } from 'react';
import { Plus, MessageSquare, ChevronDown, ChevronUp, Trash2, Edit2, Send, X } from 'lucide-react';
import { API_URL } from '../config';

const CATEGORIES = ['IT', 'RH', 'Matériel', 'Logistique', 'Finance', 'Autre'];
const PRIORITES = ['Basse', 'Normale', 'Haute', 'Urgente'];
const STATUTS = ['Ouverte', 'En cours', 'Résolue', 'Fermée'];

const prioriteColors = {
  Basse: 'bg-elevated-1 text-text-secondary',
  Normale: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700',
  Haute: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
  Urgente: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
};
const statutColors = {
  Ouverte: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  'En cours': 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  Résolue: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  Fermée: 'bg-elevated-1 text-text-secondary',
};

export default function Demandes({ token, user }) {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [commentaires, setCommentaires] = useState({});
  const [newComment, setNewComment] = useState({});
  const [filterStatut, setFilterStatut] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [form, setForm] = useState({ categorie: 'IT', titre: '', description: '', priorite: 'Normale' });
  const [editStatut, setEditStatut] = useState({});

  const isManager = ['Patron', 'Super Manager', 'Manager'].includes(user?.role_name);

  const fetchDemandes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/demandes`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setDemandes(Array.isArray(data) ? data : []);
    } catch (e) { setDemandes([]); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchDemandes(); }, [fetchDemandes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titre.trim()) return;
    const res = await fetch(`${API_URL}/demandes`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    if (res.ok) {
      setForm({ categorie: 'IT', titre: '', description: '', priorite: 'Normale' });
      setShowForm(false);
      fetchDemandes();
    }
  };

  const handleStatutChange = async (id, statut) => {
    await fetch(`${API_URL}/demandes/${id}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ statut }),
    });
    fetchDemandes();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    await fetch(`${API_URL}/demandes/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchDemandes();
  };

  const loadCommentaires = async (id) => {
    const res = await fetch(`${API_URL}/demandes/${id}/commentaires`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setCommentaires(prev => ({ ...prev, [id]: data }));
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      loadCommentaires(id);
    }
  };

  const sendComment = async (id) => {
    const contenu = newComment[id]?.trim();
    if (!contenu) return;
    await fetch(`${API_URL}/demandes/${id}/commentaires`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ contenu }),
    });
    setNewComment(prev => ({ ...prev, [id]: '' }));
    loadCommentaires(id);
  };

  const filtered = demandes.filter(d => {
    if (filterStatut && d.statut !== filterStatut) return false;
    if (filterCat && d.categorie !== filterCat) return false;
    return true;
  });

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Demandes</h1>
          <p className="text-text-muted text-sm mt-1">Gestion des demandes internes</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={16} />
          Nouvelle demande
        </button>
      </div>

      {/* Formulaire création */}
      {showForm && (
        <div className="bg-surface rounded-xl border border-border-subtle p-5 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-text-primary">Nouvelle demande</h2>
            <button onClick={() => setShowForm(false)}><X size={18} className="text-text-muted" /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Catégorie</label>
                <select className="w-full border border-border-strong rounded-lg px-3 py-2 text-sm"
                  value={form.categorie} onChange={e => setForm({ ...form, categorie: e.target.value })}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Priorité</label>
                <select className="w-full border border-border-strong rounded-lg px-3 py-2 text-sm"
                  value={form.priorite} onChange={e => setForm({ ...form, priorite: e.target.value })}>
                  {PRIORITES.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Titre *</label>
              <input className="w-full border border-border-strong rounded-lg px-3 py-2 text-sm"
                placeholder="Titre de la demande" value={form.titre}
                onChange={e => setForm({ ...form, titre: e.target.value })} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
              <textarea className="w-full border border-border-strong rounded-lg px-3 py-2 text-sm resize-none"
                rows={3} placeholder="Décrivez votre demande..." value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm text-text-secondary border border-border-strong rounded-lg hover:bg-elevated-1">
                Annuler
              </button>
              <button type="submit"
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Créer la demande
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filtres */}
      <div className="flex gap-3 mb-4">
        <select className="border border-border-strong rounded-lg px-3 py-1.5 text-sm bg-surface"
          value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
          <option value="">Tous les statuts</option>
          {STATUTS.map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="border border-border-strong rounded-lg px-3 py-1.5 text-sm bg-surface"
          value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="">Toutes les catégories</option>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <span className="ml-auto text-sm text-text-muted self-center">{filtered.length} demande(s)</span>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-12 text-text-muted">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-text-muted">Aucune demande</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(d => (
            <div key={d.id} className="bg-surface rounded-xl border border-border-subtle shadow-sm overflow-hidden">
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-medium text-text-muted bg-elevated-1 px-2 py-0.5 rounded">{d.categorie}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${prioriteColors[d.priorite]}`}>{d.priorite}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statutColors[d.statut]}`}>{d.statut}</span>
                    </div>
                    <h3 className="font-semibold text-text-primary">{d.titre}</h3>
                    {d.description && <p className="text-sm text-text-muted mt-1 line-clamp-2">{d.description}</p>}
                    <div className="flex items-center gap-3 mt-2 text-xs text-text-muted">
                      <span>Par {d.demandeur_nom}</span>
                      <span>·</span>
                      <span>{new Date(d.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isManager && (
                      <select
                        className="text-xs border border-border-strong rounded px-2 py-1 bg-surface"
                        value={d.statut}
                        onChange={e => handleStatutChange(d.id, e.target.value)}
                      >
                        {STATUTS.map(s => <option key={s}>{s}</option>)}
                      </select>
                    )}
                    <button onClick={() => toggleExpand(d.id)}
                      className="p-1.5 text-text-muted hover:text-blue-600 dark:text-blue-400 rounded">
                      <MessageSquare size={16} />
                    </button>
                    {(isManager || d.demandeur_id === user?.employee_id) && (
                      <button onClick={() => handleDelete(d.id)}
                        className="p-1.5 text-text-muted hover:text-red-600 dark:text-red-400 rounded">
                        <Trash2 size={14} />
                      </button>
                    )}
                    <button onClick={() => toggleExpand(d.id)} className="text-text-muted hover:text-text-secondary">
                      {expandedId === d.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Commentaires */}
              {expandedId === d.id && (
                <div className="border-t border-border-subtle bg-elevated-1 p-4">
                  <h4 className="text-sm font-medium text-text-secondary mb-3">Commentaires</h4>
                  <div className="space-y-2 mb-3">
                    {(commentaires[d.id] || []).length === 0 ? (
                      <p className="text-sm text-text-muted italic">Aucun commentaire</p>
                    ) : (
                      (commentaires[d.id] || []).map(c => (
                        <div key={c.id} className="bg-surface rounded-lg p-3 border border-border-subtle">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-text-secondary">{c.auteur_nom}</span>
                            <span className="text-xs text-text-muted">{new Date(c.created_at).toLocaleDateString('fr-FR')}</span>
                          </div>
                          <p className="text-sm text-text-secondary">{c.contenu}</p>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input
                      className="flex-1 border border-border-strong rounded-lg px-3 py-2 text-sm"
                      placeholder="Ajouter un commentaire..."
                      value={newComment[d.id] || ''}
                      onChange={e => setNewComment(prev => ({ ...prev, [d.id]: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && sendComment(d.id)}
                    />
                    <button onClick={() => sendComment(d.id)}
                      className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                      <Send size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
